import { useState, useContext } from "react";
import {
  Modal,
  Form,
  Input,
  Row,
  Col,
  Typography,
  Checkbox,
  Tag,
} from "antd";

import MainContext from "@/layouts/MainLayout/context";
import CustomSelect from "@/components/inputs/CustomSelect";
import DigitsDatePicker from "@/components/inputs/DigitsDatePicker";

import { createItem, updateItem } from "@/api/sprints";

import dayjs from "dayjs";

import { STATUS_OPTIONS } from "@/constants/sprints";

const { Text } = Typography;
const miniDateFormat = "DD.MM";
const durationOptions = [
  {
    label: "Неделя",
    value: "1_week",
  },
  {
    label: "2 недели",
    value: "2_week",
  },
  {
    label: "3 недели",
    value: "3_week",
  },
  {
    label: "4 недели",
    value: "4_week",
  },
  {
    label: "Другое",
    value: "other",
  },
];

const renderStatusTag = (statuses) => (props) => {
  const { label, value } = props;
  const status = statuses.find((s) => s.uuid === value);
  const icon = STATUS_OPTIONS.find(
    (item) => item.value === status?.style_key
  )?.icon;

  return (
    <Tag icon={icon} color={status?.style_key} className="tag-with-icon-color">
      {label}
    </Tag>
  );
};

export const renderStatusOption = (option) => {
  const icon = STATUS_OPTIONS.find(
    (item) => item.value === option.data.style_key
  )?.icon;

  return (
    <Tag
      icon={icon}
      color={option.data.style_key}
      className="tag-with-icon-color"
    >
      {option.label}
    </Tag>
  );
};

const SprintModal = ({
  data,
  visible,
  onSave,
  onCancel,
  unit_uuid,
  units = [],
  statuses = [],
}) => {
  const [form] = Form.useForm();
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const { config } = useContext(MainContext);
  const sprintName = config?.entities?.sprint?.name?.singular || "Спринт";

  const todayDate = dayjs();
  const todayPlusOneWeek = todayDate.add(7, "day");
  const defaultUnit = units[0];
  const defaultStatus = statuses.find(({ key }) => key === "not_started");
  const workStatus = statuses.find(({ key }) => key === "in_progress");

  const initialValue = {
    ...data,
    start_date: data?.uuid
      ? data.start_date
        ? dayjs(data.start_date)
        : null
      : todayDate,
    end_date: data?.end_date ? dayjs(data.end_date) : todayPlusOneWeek,
    unit_uuid: data?.unit_uuid || unit_uuid || defaultUnit.uuid,
    duration: data?.duration || durationOptions[0].value,
    name:
      data?.name ||
      `${sprintName} ${todayDate.format(
        miniDateFormat
      )}-${todayPlusOneWeek.format(miniDateFormat)}`,
    status_uuid: data?.status_uuid || defaultStatus.uuid,
    is_custom_name: data?.is_custom_name || false,
    hire: false,
  };

  const duration = Form.useWatch("duration", form);

  // Функция пересчитывает и устанавливает даты начала и конца спринта, и меняет название
  const onChangeDate = (startDate, plusWeeks) => {
    if (startDate && plusWeeks) {
      const newEndDate = startDate
        .add(parseInt(plusWeeks, 10), "week")
        .subtract(1, "day");

      const isCustomName = form.getFieldValue("is_custom_name");

      form.setFieldsValue({
        start_date: startDate,
        end_date: newEndDate,
        ...(!isCustomName && {
          name: `${sprintName} ${startDate.format(
            miniDateFormat
          )}-${newEndDate.format(miniDateFormat)}`,
        }),
      });
    }
  };

  // Функция устанавливает новое название спринта в зависимости от дат начала и конца
  const setNewName = () => {
    const isCustomName = form.getFieldValue("is_custom_name");
    if (isCustomName) return;

    const startDate = form.getFieldValue("start_date");
    const endDate = form.getFieldValue("end_date");

    form.setFieldsValue({
      name: `${sprintName} ${
        startDate ? startDate.format(miniDateFormat) : "..."
      }-${endDate ? endDate.format(miniDateFormat) : "..."}`,
    });
  };

  const onValuesChange = async (changedValues) => {
    if ("duration" in changedValues && changedValues.duration !== "other") {
      // Если изменилась длительность и она не "другое", пересчитываем дату окончания и название
      onChangeDate(
        form.getFieldValue("start_date") ?? todayDate,
        changedValues.duration.split("_")[0]
      );
    }
    if ("start_date" in changedValues) {
      if (duration !== "other") {
        // Если изменилась дата начала и длительность не "другое", пересчитываем дату окончания и название
        onChangeDate(changedValues.start_date, duration.split("_")[0]);
      } else {
        // Если дату начала поменяли и она стала позже даты окончания, сбрасываем дату окончания и меняем название
        const startDate = changedValues.start_date;
        const endDate = form.getFieldValue("end_date");

        if (endDate && startDate?.isAfter(endDate)) {
          form.setFieldsValue({
            end_date: null,
          });
        }

        setNewName();
      }
    }
    if ("end_date" in changedValues) {
      // Если изменилась дата окончания, сбрасываем дату начала, если она позже даты окончания, и меняем название
      const startDate = form.getFieldValue("start_date");
      const endDate = changedValues.end_date;

      if (endDate && startDate?.isAfter(endDate)) {
        form.setFieldsValue({
          start_date: null,
        });
      }

      setNewName();
    }
    if ("name" in changedValues) {
      // Если поменяли имя, то ставим флаг, что название теперь кастомное
      form.setFieldsValue({
        is_custom_name: true,
      });
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setError(null);
    onCancel();
  };

  const onOk = () => {
    form
      .validateFields()
      .then((values) => {
        handleSave(values);
      })
      .catch((info) => {
        console.log("Ошибка валидации:", info);
      });
  };

  const handleSave = async (values) => {
    setError(null);
    setIsLoading(true);

    try {
      if (data?.uuid) {
        await updateItem({
          ...data,
          ...values,
          start_date: values.start_date?.format().split("T")[0],
          end_date: values.end_date?.format().split("T")[0],
        });
      } else {
        await createItem({
          ...values,
          status_uuid: values.hire ? workStatus.uuid : defaultStatus.uuid,
          start_date: values.start_date?.format().split("T")[0],
          end_date: values.end_date?.format().split("T")[0],
        });
      }

      onSave();
      form.resetFields();
    } catch (error) {
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };
  return (
    <Modal
      maskClosable={false}
      title={data?.uuid ? "Редактирование" : "Создание"}
      open={visible}
      onOk={onOk}
      onCancel={handleCancel}
      okText={data?.uuid ? "Сохранить" : "Добавить"}
      cancelText="Отмена"
      okButtonProps={{ loading: isLoading }}
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={initialValue}
        onFinish={handleSave}
        onValuesChange={onValuesChange}
      >
        <Form.Item name="is_custom_name" hidden></Form.Item>

        <Form.Item
          name="unit_uuid"
          label="Рабочая группа"
          rules={[{ required: true, message: "Пожалуйста, заполните поле" }]}
        >
          <CustomSelect
            placeholder="Начните вводить"
            options={units}
            fieldNames={{
              label: "name",
              value: "uuid",
            }}
            showSearch
            optionFilterProp="name"
            allowClear={false}
            disabled={data?.uuid}
          />
        </Form.Item>

        <Row gutter={{ xs: 10, sm: 10, md: 20, lg: 20 }}>
          <Col xs={24} sm={12}>
            <Form.Item
              name="duration"
              label="Длительность"
              rules={[
                { required: true, message: "Пожалуйста, заполните поле" },
              ]}
            >
              <CustomSelect options={durationOptions} />
            </Form.Item>
          </Col>
          {data?.uuid && (
            <Col xs={24} sm={12}>
              <Form.Item
                name="status_uuid"
                label="Статус"
                rules={[
                  {
                    required: data?.uuid,
                    message: "Пожалуйста, заполните поле",
                  },
                ]}
              >
                <CustomSelect
                  options={statuses}
                  fieldNames={{ value: "uuid" }}
                  optionFilterProp="name"
                  labelRender={renderStatusTag(statuses)}
                  optionRender={renderStatusOption}
                />
              </Form.Item>
            </Col>
          )}
        </Row>

        <Row gutter={{ xs: 10, sm: 10, md: 20, lg: 20 }}>
          <Col xs={24} sm={12}>
            <Form.Item
              name="start_date"
              label="Дата начала"
              rules={[
                { required: true, message: "Пожалуйста, заполните поле" },
              ]}
            >
              <DigitsDatePicker
                style={{ width: "100%" }}
                allowClear={false}
                onChange={(date) => {
                  form.setFieldValue("start_date", date);
                }}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              name="end_date"
              label="Дата окончания"
              rules={[
                { required: true, message: "Пожалуйста, заполните поле" },
              ]}
            >
              <DigitsDatePicker
                style={{ width: "100%" }}
                allowClear={false}
                onChange={(date) => {
                  form.setFieldValue("end_date", date);
                }}
                disabled={duration !== "other"}
              />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          name="name"
          label="Название"
          rules={[{ required: true, message: "Пожалуйста, заполните поле" }]}
        >
          <Input />
        </Form.Item>

        {!data?.uuid && (
          <Form.Item
            name="hire"
            valuePropName="checked"
            style={{ marginBottom: 0 }}
          >
            <Checkbox>Взять в работу</Checkbox>
          </Form.Item>
        )}

        {error && <Text type="danger">{error}</Text>}
      </Form>
    </Modal>
  );
};

export default SprintModal;
